import { Box } from "@mui/material";
import { OrionInput } from "@orion/OrionComponents/OrionInput";
import { useForm } from "@tanstack/react-form";
import {
  Link,
  createFileRoute,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { type FormEvent, useLayoutEffect, useState } from "react";
import { z } from "zod";

import type { Organization } from "@/types/auth";

import AuthLayout from "@/components/AuthLayout/AuthLayout";
import LoginFormSubmitButton from "@/components/LoginFormSubmitButton/LoginFormSubmitButton";
import { useAuth } from "@/contexts/AuthContext";
import {
  authLinkStyles,
  labelStyles,
  sxTextFieldStyles,
} from "@/routes/-authStyles";
import { resolveLoginEntry } from "@/routes/-loginRedirect";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const isDevelopment = import.meta.env.VITE_ENVIRONMENT === "development";

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
  reauth: z.boolean().optional(), // see resolveLoginEntry
});

export const Route = createFileRoute("/login")({
  validateSearch: zodValidator(loginSearchSchema),
  beforeLoad: ({ context, search }) => {
    // Skip auth redirect in development mode
    if (isDevelopment) {
      return;
    }

    const isAuthenticated = context.auth?.isAuthenticated === true;
    const entry = resolveLoginEntry({ isAuthenticated, ...search });
    // The hop is the component's alone: beforeLoad reruns (boot loads twice),
    // and a second navigation aborts one that may have spent the refresh cookie.
    if (entry.kind === "spa") throw redirect({ to: entry.to });
  },
  component: LoginComponent,
});

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

function LoginComponent() {
  const auth = useAuth();
  const router = useRouter();
  const search = Route.useSearch();
  const [orgSelection, setOrgSelection] = useState<{
    pendingAuthenticationToken: string;
    organizations: Organization[];
  } | null>(null);

  const { isAuthenticated, resetSession } = auth;

  // Tanstack recommends redirecting from auth with this pattern.
  useLayoutEffect(() => {
    const { redirect, reauth } = search;
    const entry = resolveLoginEntry({ isAuthenticated, redirect, reauth });
    if (entry.kind === "document") {
      // replace: Back must not run the hop again.
      window.location.replace(entry.href);
    } else if (entry.kind === "spa") {
      router.history.push(entry.to);
    } else if (entry.kind === "reset") {
      // Not navigating yet: auth still says signed in, so clearing reauth now
      // would bounce straight back to the hop. The signed-out rerun clears it.
      resetSession();
    } else if (reauth) {
      // Clear the flag so the login that follows redirects normally.
      void router.navigate({
        to: "/login",
        search: { redirect },
        replace: true,
      });
    }
  }, [isAuthenticated, resetSession, search, router]);

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    validators: {
      onSubmit: loginSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const result = await auth.login({
          email: value.email,
          password: value.password,
        });
        if (result.kind === "organization_selection_required") {
          setOrgSelection({
            pendingAuthenticationToken: result.pendingAuthenticationToken,
            organizations: result.organizations,
          });
          return;
        }
        await router.invalidate();
      } catch (error: unknown) {
        enqueueErrorSnackbar(error, "Login failed");
      }
    },
  });

  const handleSelectOrganization = async (organizationId: string) => {
    if (!orgSelection) return;
    try {
      await auth.selectOrganization(
        organizationId,
        orgSelection.pendingAuthenticationToken,
      );
      await router.invalidate();
    } catch (error: unknown) {
      enqueueErrorSnackbar(error, "Organization selection failed");
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void form.handleSubmit();
  };

  return (
    <AuthLayout
      title={orgSelection ? "Select Organization" : "Log in to Queen One"}
      onSubmit={orgSelection ? undefined : handleSubmit}
    >
      {orgSelection ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {orgSelection.organizations.map((org) => (
            <LoginFormSubmitButton
              key={org.id}
              type="button"
              label={org.name}
              onClick={() => {
                void handleSelectOrganization(org.id);
              }}
              disabled={auth.isLoading}
            />
          ))}
        </Box>
      ) : (
        <>
          <form.Field name="email">
            {(field) => (
              <OrionInput
                field={field}
                label="Email address"
                type="email"
                placeholder="Enter your email"
                sx={sxTextFieldStyles}
                labelStyles={labelStyles}
              />
            )}
          </form.Field>
          <form.Field name="password">
            {(field) => (
              <OrionInput
                field={field}
                label="Password"
                type="password"
                placeholder="Enter your password"
                sx={sxTextFieldStyles}
                labelStyles={labelStyles}
              />
            )}
          </form.Field>

          <LoginFormSubmitButton
            type="submit"
            label="Enter the Fortress"
            disabled={form.state.isSubmitting}
          />

          <Box component={Link} to="/forgot-password" sx={authLinkStyles}>
            Forgot password?
          </Box>
        </>
      )}
    </AuthLayout>
  );
}
